// src/pages/Panel/components/Stats/PendingTable.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getAllPendings } from '../../../../../../crud/management_api/local_sessions';
import './PendingTable.scss';
import ReceiptDetails from '../../../../../Restaurant/Caja/Receipts/components/Table/components/ReceiptDetails/ReceiptDetails';
import CloseCaja from '../../../../../Restaurant/Caja/CajasManagement/components/IndividualCaja/components/CloseCaja/CloseCaja';
import { Receipt } from '../../../../../Restaurant/Caja/types';
import ModalPendingCash from './components/ModalPendingCash/ModalPendingCash';
import { toast } from 'react-toastify';
import { BiDish } from "react-icons/bi";
import { PiNewspaperClipping } from "react-icons/pi";
import { FaCreditCard } from "react-icons/fa";
import { FaMoneyBill } from "react-icons/fa";
import { MdDeliveryDining } from "react-icons/md";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";
import { FaLock, FaList } from "react-icons/fa";
import CreateCajaRecord from '../../../../../Restaurant/Caja/CajasManagement/components/Arqueos/components/CreateCajaRecord/CreateCajaRecord';

type PendingKind = 'Pedido' | 'Recibo' | 'Caja' | 'Arqueo' | 'Efectivo';

interface PendingItem {
  id: number | string;
  kind: PendingKind;
  data: any;
}

interface PendingCategory {
  id: string;
  title: string;
  items: PendingItem[];
  priority: number;
  icon: string;
  description: string;
}

interface PendingTableProps {
  localSessionId?: number;
  setActioncaja?: (closing: boolean) => void;
  onNavigate?: () => void; // Callback to execute when navigating
  pendingsData?: any | null; // Pendings data passed from parent
  setPendingsData?: (data: any) => void; // Setter for pendings data
  autoExpandFirst?: boolean; // Auto-expand first category (e.g., when coming from end session)
}

export const PendingTable: React.FC<PendingTableProps> = ({ localSessionId, setActioncaja, onNavigate, pendingsData, setPendingsData, autoExpandFirst = false }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<PendingCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [detailReceipt, setDetailReceipt] = useState<Receipt | null>(null);
  const [showCloseCaja, setShowCloseCaja] = useState(false);
  const [detailCaja, setDetailCaja] = useState<any | null>(null);
  const [showPendingCash, setShowPendingCash] = useState(false);
  const [showArqueoModal, setShowArqueoModal] = useState(false);

  // Category configuration - modular and extensible
  // Priority system: Only Caja (Cash Closing) and Arqueo (Cash Count) have restrictions
  // Caja cannot be closed if there's pending cash (Efectivo)
  // Arqueo must be done after all other categories are completed
  const getCategoryConfig = (): Omit<PendingCategory, 'items'>[] => [
    {
      id: 'pedidos',
      title: 'Pendientes pedidos',
      priority: 1, // No restrictions
      icon: '', // Will use BiDish component
      description: 'Acciones pendientes en pedidos'
    },
    {
      id: 'recibos',
      title: 'Pendientes recibos',
      priority: 1, // No restrictions
      icon: '', // Will use RiFilePaperFill component
      description: 'Recibos activos sin cerrar'
    },
    {
      id: 'efectivo',
      title: 'Efectivo pendiente',
      priority: 1, // No restrictions - must be handled before closing cash register
      icon: '', // Will use FaMoneyBill component
      description: 'Efectivo por aprobar'
    },
    {
      id: 'arqueo',
      title: 'Arqueo pendiente',
      priority: 3, // Restricted: Must be done after all other categories
      icon: '', // Will use FaChartLine component
      description: 'Cierre de sesión pendiente'
    }
  ];

  // Process API data into categories - modular processor
  const processApiDataToCategories = useCallback((data: any): PendingCategory[] => {
    const categoryConfig = getCategoryConfig();
    const newCategories: PendingCategory[] = [];

    categoryConfig.forEach(config => {
      const items: PendingItem[] = [];

      switch (config.id) {
        case 'pedidos':
          (data.pending_pedidos ?? []).forEach((p: any) =>
            items.push({ id: p.pedido_id, kind: 'Pedido', data: p })
          );
          break;
        case 'recibos':
          (data.pending_receipts ?? []).forEach((r: any) =>
            items.push({ id: r.receipt_id, kind: 'Recibo', data: r })
          );
          break;
        case 'efectivo':
          if (data.pending_cash) {
            if (Array.isArray(data.pending_cash) && data.pending_cash.length > 0) {
              // Create one item per cash transaction
              data.pending_cash.forEach((cashItem: any, index: number) => {
                items.push({ 
                  id: `CASH-${index}`, 
                  kind: 'Efectivo', 
                  data: cashItem 
                });
              });
            } else if (typeof data.pending_cash === 'object' && Object.keys(data.pending_cash).length > 0) {
              // Single cash object
              items.push({ 
                id: 'CASH', 
                kind: 'Efectivo', 
                data: data.pending_cash 
              });
            }
          }
          break;
        case 'arqueo':
          if (data.pending_arqueo) {
            items.push({ id: 'ARQ', kind: 'Arqueo', data: null });
          }
          break;
      }

      // Only add category if it has items
      if (items.length > 0) {
        newCategories.push({
          ...config,
          items
        });
      }
    });

    return newCategories.sort((a, b) => a.priority - b.priority);
  }, []);

  useEffect(() => {
    // If pendingsData is provided from parent, use it directly
    if (pendingsData !== undefined) {
      if (pendingsData === null) {
        setCategories([]);
        setExpandedCategories(new Set());
        setError(null);
        setLoading(false);
        return;
      }
      const newCategories = processApiDataToCategories(pendingsData);
      setCategories(newCategories);
      // Auto-expand first category if autoExpandFirst is true
      const initialExpanded: Set<string> = autoExpandFirst && newCategories.length > 0 
        ? new Set([newCategories[0].id]) 
        : new Set();
      setExpandedCategories(initialExpanded);
      setError(null);
      setLoading(false);
      return;
    }

    // Otherwise, fetch from API (legacy behavior for when used outside Panel)
    if (localSessionId == null) {
      setCategories([]);
      setExpandedCategories(new Set());
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    getAllPendings(localSessionId)
      .then(({ data }) => {
        const newCategories = processApiDataToCategories(data);
        setCategories(newCategories);
        // Auto-expand first category if autoExpandFirst is true
        const initialExpanded: Set<string> = autoExpandFirst && newCategories.length > 0 
          ? new Set([newCategories[0].id]) 
          : new Set();
        setExpandedCategories(initialExpanded);
        setError(null);
      })
      .catch(() => setError('Error cargando pendientes'))
      .finally(() => setLoading(false));
  }, [localSessionId, processApiDataToCategories, pendingsData, autoExpandFirst]);

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Handle keyboard navigation for categories
  const handleCategoryKeyDown = (event: React.KeyboardEvent, categoryId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleCategory(categoryId);
    }
  };

  // Check if an item is blocked based on specific business rules
  const isItemBlocked = (item: PendingItem): boolean => {
    // Find the category this item belongs to
    const itemCategory = categories.find(cat => 
      cat.items.some(catItem => catItem.kind === item.kind && catItem.id === item.id)
    );
    
    if (!itemCategory) return false;
    
    // Apply specific blocking rules:
    
    // 1. Cash Closing (Caja) is blocked if there's pending cash (Efectivo)
    if (item.kind === 'Caja') {
      const hasEffectivo = categories.some(cat => 
        cat.id === 'efectivo' && cat.items.length > 0
      );
      return hasEffectivo;
    }
    
    // 2. Cash Count (Arqueo) is blocked if there are any other pending categories
    if (item.kind === 'Arqueo') {
      const otherCategories = categories.filter(cat => 
        cat.id !== 'arqueo' && cat.items.length > 0
      );
      return otherCategories.length > 0;
    }
    
    // 3. All other items (Pedidos, Recibos, Efectivo) are never blocked
    return false;
  };

  // Get the list of blocking categories for an item
  const getBlockingCategories = (item: PendingItem): string[] => {
    // Apply specific blocking rules:
    
    // 1. Cash Closing (Caja) is blocked by pending cash (Efectivo)
    if (item.kind === 'Caja') {
      const efectivoCategory = categories.find(cat => 
        cat.id === 'efectivo' && cat.items.length > 0
      );
      return efectivoCategory ? ['Efectivo pendiente'] : [];
    }
    
    // 2. Cash Count (Arqueo) is blocked by any other pending categories
    if (item.kind === 'Arqueo') {
      const blockingCategories = categories.filter(cat => 
        cat.id !== 'arqueo' && cat.items.length > 0
      );
      return blockingCategories.map(cat => cat.title);
    }
    
    // 3. All other items are never blocked
    return [];
  };

  // Bloquea la acción si hay pendientes previos por familia/categoría
  const handleAction = (item: PendingItem) => {
    // Check if item is blocked
    if (isItemBlocked(item)) {
      const blockingCategories = getBlockingCategories(item);
      toast.error(
        `Debes atender primero: ${blockingCategories.join(', ')} antes de realizar esta acción.`
      );
      return;
    }
    
    switch (item.kind) {
      case 'Pedido':
        handlePedidoRedirect(item.data.pedido_type_id);
        onNavigate?.(); // Call the callback when navigating
        break;
      case 'Recibo':
        setDetailReceipt(item.data);
        setShowReceipt(true);
        break;
      case 'Arqueo':
        navigate({ pathname: '/App/CajasManagement', search: '?tab=arqueos' });
        onNavigate?.(); // Call the callback when navigating
        break;
      case 'Efectivo':
        setShowPendingCash(true);
        break;
    }
  };

  const handleReceiptSettled = (receiptId: number) => {
    setCategories(prev =>
      prev.map(category => ({
        ...category,
        items: category.items.filter(item => !(item.kind === 'Recibo' && item.id === receiptId))
      })).filter(category => category.items.length > 0)
    );
  };

  const handlePedidoRedirect = (type: number) => {
    const paths: Record<number, string> = {
      1: '/App/Restaurant',
      2: '/App/Autoservicio',
      3: '/App/Restaurant?view=pedidos',
      4: '/App/VentaCaja',
      5: '/App/Restaurant', // Mesa Virtual (MV)
    };
    navigate(paths[type]);
  };

  if (loading) {
    return (
      <div className="pending-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spinner animation="border" variant="warning"  />
        </div>
    );
  }
  if (error) return <div className="text-danger">{error}</div>;

  // Validación para Arqueo - debe ser la última familia en ejecutarse
  const validationArqueo = (): boolean => {
    // Find categories other than Arqueo that have pending items
    const otherCategoriesWithItems = categories.filter(cat => 
      cat.id !== 'arqueo' && cat.items.length > 0
    );
    
    if (otherCategoriesWithItems.length > 0) {
      const pendingFamilies = otherCategoriesWithItems.map(cat => cat.title);
        
      toast.error(
        `Hay familias pendientes que deben ser atendidas antes del arqueo: ${pendingFamilies.join(', ')}.`
      );
      return false;
    }
    return true;
  }

  // Get item details for display with specific context information
  const getItemDetails = (item: PendingItem) => {
    const baseDetails: {
      title: React.ReactNode;
      subtitle: string;
      metaInfo: string;
      timestamp: string;
      actionText: string;
    } = {
      title: '',
      subtitle: '',
      metaInfo: '',
      timestamp: '',
      actionText: ''
    };

    switch (item.kind) {
      case 'Pedido': {
        let orderTypeIcon: React.ReactNode;
        if (item.data.pedido_type_id === 1) {
          orderTypeIcon = <PiNewspaperClipping style={{ color: 'var(--mediopunto-orange, #FC7556)', verticalAlign: 'middle' }} size={18} />;
        } else if (item.data.pedido_type_id === 2) {
          orderTypeIcon = '🔄';
        } else if (item.data.pedido_type_id === 3) {
          orderTypeIcon = <MdDeliveryDining style={{ color: 'var(--mediopunto-orange, #FC7556)', verticalAlign: 'middle' }} size={18} />;
        } else if (item.data.pedido_type_id === 4) {
          orderTypeIcon = '💰';
        } else if (item.data.pedido_type_id === 5) {
          orderTypeIcon = <PiNewspaperClipping style={{ color: 'var(--mediopunto-orange, #FC7556)', verticalAlign: 'middle' }} size={18} />;
        } else {
          orderTypeIcon = '📋';
        }

        baseDetails.title = <>{orderTypeIcon} Pedido #{item.data.pedido_id}</>;
        baseDetails.subtitle = item.data.pedido_type_name || 'Pedido';

        // Build meta information for orders
        const metaParts = [];
        if (item.data.mesa_num) {
          metaParts.push(`Mesa ${item.data.mesa_num}`);
        }
        if (item.data.user_name) {
          metaParts.push(`${item.data.user_name}`);
        }
        if (item.data.client_name) {
          metaParts.push(`Cliente: ${item.data.client_name}`);
        }
        baseDetails.metaInfo = metaParts.join(' • ');
        baseDetails.timestamp = item.data.local_timestamp ? 
          new Date(item.data.local_timestamp).toLocaleTimeString('es-ES', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : '';
        baseDetails.actionText = 'Ir';
        break;
      }

      case 'Recibo':
        baseDetails.title = `Recibo #${item.data.receipt_number || item.data.receipt_id}`;
        baseDetails.subtitle = item.data.pedido_type_name || 'Recibo';
        
        const receiptMeta = [];
        if (item.data.mesa_num) {
          receiptMeta.push(`Mesa ${item.data.mesa_num}`);
        }
        if (item.data.user_names && item.data.user_names.length > 0) {
          receiptMeta.push(item.data.user_names.join(', '));
        }
        if (item.data.total_due) {
          receiptMeta.push(`$${item.data.total_due.toLocaleString()}`);
        }
        baseDetails.metaInfo = receiptMeta.join(' • ');
        baseDetails.timestamp = item.data.local_timestamp ? 
          new Date(item.data.local_timestamp).toLocaleTimeString('es-ES', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : '';
        baseDetails.actionText = 'Ver detalle';
        break;



      case 'Efectivo':
        baseDetails.title = 'Efectivo pendiente';
        baseDetails.subtitle = 'Por aprobar';
        baseDetails.metaInfo = item.data.user_name ? 
          `${item.data.user_name} • $${item.data.monto?.toLocaleString()}` : '';
        baseDetails.actionText = 'Ver efectivo';
        break;

      case 'Arqueo':
        baseDetails.title = 'Arqueo pendiente';
        baseDetails.subtitle = 'Cierre de sesión';
        baseDetails.metaInfo = 'Requiere atención';
        baseDetails.actionText = 'Crear Arqueo';
        break;

      default:
        baseDetails.title = item.kind;
        baseDetails.actionText = 'Ir';
    }

    return baseDetails;
  };

  return (
    <>
      <div className="pending-table-container">
        <div className='scroll-container-table pending-table'>
          {categories.length === 0 ? (
            <div className="no-pendings">
              <div className="no-pendings-icon">📋</div>
              <div className="no-pendings-text">Todo al día</div>
              <div className="no-pendings-subtext">No hay acciones pendientes</div>
            </div>
          ) : (
            <div className="pending-categories-grid">
              {categories.map(category => {
                // Check if this category is blocked based on specific business rules
                let isCategoryBlocked = false;
                let blockingMessage = category.description;
                
                if (category.id === 'caja') {
                  // Cash Closing is blocked if there's pending cash
                  const hasEffectivo = categories.some(cat => 
                    cat.id === 'efectivo' && cat.items.length > 0
                  );
                  isCategoryBlocked = hasEffectivo;
                  if (isCategoryBlocked) {
                    blockingMessage = 'Debes procesar el efectivo pendiente primero';
                  }
                } else if (category.id === 'arqueo') {
                  // Cash Count is blocked if there are other pending categories
                  const otherCategories = categories.filter(cat => 
                    cat.id !== 'arqueo' && cat.items.length > 0
                  );
                  isCategoryBlocked = otherCategories.length > 0;
                  if (isCategoryBlocked) {
                    blockingMessage = 'Completa todas las otras acciones primero';
                  }
                }
                
                return (
                  <div key={category.id} className={`pending-category ${isCategoryBlocked ? 'blocked' : ''}`}>
                    <div 
                      className="pending-category-header" 
                      onClick={() => toggleCategory(category.id)}
                      onKeyDown={(e) => handleCategoryKeyDown(e, category.id)}
                      tabIndex={0}
                      role="button"
                      aria-expanded={expandedCategories.has(category.id)}
                      aria-controls={`category-content-${category.id}`}
                    >
                      <div className="pending-category-info">
                        <div className="pending-category-icon">
                          {category.id === 'pedidos'
                            ? <BiDish style={{ color: 'var(--mediopunto-orange, #FC7556)' }} size={22} />
                            : category.id === 'recibos'
                                ? <FaCreditCard style={{ color: 'var(--mediopunto-orange, #FC7556)' }} size={20} />
                                : category.id === 'efectivo'
                                  ? <FaMoneyBill style={{ color: 'var(--mediopunto-orange, #FC7556)' }} size={20} />
                                  : category.id === 'arqueo'
                                    ? <FaList style={{ color: 'var(--mediopunto-orange, #FC7556)' }} size={20} />
                                    : category.icon}
                        </div>
                        <div className="pending-category-details">
                          <div className="pending-category-title">
                            {category.title}
                            <span className={`pending-category-count ${isCategoryBlocked ? 'blocked' : ''}`}>
                              {isCategoryBlocked && <FaLock size={10} style={{ marginRight: '4px' }} />}
                              {category.items.length}
                            </span>
                          </div>
                          <div className="pending-category-description">
                            {blockingMessage}
                          </div>
                        </div>
                      </div>
                      <div className={`pending-category-arrow ${expandedCategories.has(category.id) ? 'expanded' : ''}`}>
                        {expandedCategories.has(category.id) ? <FaChevronDown /> : <FaChevronRight />}
                      </div>
                    </div>
                    
                    {expandedCategories.has(category.id) && (
                      <div className="pending-category-content" id={`category-content-${category.id}`}>
                        {category.items.map(item => {
                          const details = getItemDetails(item);
                          const isBlocked = isItemBlocked(item);
                          
                          return (
                            <div key={`${item.kind}-${item.id}`} className={`pending-item ${item.kind.toLowerCase()}-item ${isBlocked ? 'blocked' : ''}`}>
                              <div className="pending-item-info">
                                <div className="pending-item-header">
                                  <div className="pending-item-title">{details.title}</div>
                                  {details.timestamp && (
                                    <div className={`pending-item-timestamp ${isBlocked ? 'blocked' : ''}`}>
                                      {isBlocked && <FaLock size={8} style={{ marginRight: '3px' }} />}
                                      {details.timestamp}
                                    </div>
                                  )}
                                </div>
                                {details.subtitle && (
                                  <div className="pending-item-subtitle">{details.subtitle}</div>
                                )}
                                {details.metaInfo && (
                                  <div className="pending-item-meta">
                                    {isBlocked 
                                      ? `Bloqueado: Completa ${getBlockingCategories(item).join(', ')} primero`
                                      : details.metaInfo
                                    }
                                  </div>
                                )}
                              </div>
                              <Button
                                className={`pending-item-button ${isBlocked ? 'blocked' : ''}`}
                                size="sm"
                                variant="none"
                                disabled={isBlocked}
                                onClick={() => {
                                  if (item.kind === 'Arqueo') {
                                    if (!validationArqueo()) return;
                                    setShowArqueoModal(true);
                                    return;
                                  }
                                  handleAction(item);
                                }}
                              >
                                {isBlocked && <FaLock size={10} style={{ marginRight: '4px' }} />}
                                {details.actionText}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {detailReceipt && (
        <ReceiptDetails
          currentReceipt={detailReceipt}
          setReceipts={() => {}}
          show={showReceipt}
          onClose={() => {
            setShowReceipt(false);
            setDetailReceipt(null);
          }}
          onReceiptSettled={handleReceiptSettled}
        />
      )}

      {detailCaja && (
        <CloseCaja
          caja={detailCaja}
          cajas={categories.find(c => c.id === 'caja')?.items.map(i => i.data) || []}
          setCajas={(newList: any[]) =>
            setCategories(prev =>
              prev.map(category => 
                category.id === 'caja' 
                  ? { ...category, items: category.items.filter(i => newList.some(c => c.workstation_id === i.id)) }
                  : category
              ).filter(category => category.items.length > 0)
            )
          }
          show={showCloseCaja}
          setShow={setShowCloseCaja}
          onClose={() => {
            setShowCloseCaja(false);
            setDetailCaja(null);
          }}
          {...(setActioncaja ? { setActioncaja } : {})}
          hasPendientes={
            !!((categories.find(c => c.id === 'efectivo')?.items?.length ?? 0) > 0 || ((categories.find(c => c.id === 'pedidos')?.items?.length ?? 0) > 0) || ((categories.find(c => c.id === 'recibos')?.items?.length ?? 0) > 0))
          }
        />
      )}

      {showPendingCash && (
        <ModalPendingCash
          show={showPendingCash}
          onHide={() => {
            setShowPendingCash(false);
            // Recargar los datos de pendientes para reflejar el estado real
            if (localSessionId != null) {
              getAllPendings(localSessionId)
                .then(({ data }) => {
                  const newCategories = processApiDataToCategories(data);
                  setCategories(newCategories);
                  // Actualizar pendingsData si existe el setter
                  if (setPendingsData) {
                    setPendingsData(data);
                  }
                })
                .catch((err) => {
                  console.error('Error al recargar pendientes:', err);
                });
            }
          }}
          cajas={categories.find(c => c.id === 'efectivo')?.items.map(i => i.data) || []}
          setCajas={(newList: any[]) => {
            // Solo eliminar la categoría si realmente ya no hay efectivo pendiente
            if (!newList || newList.length === 0) {
              setCategories(prev => prev.filter(c => c.id !== 'efectivo'));
            }
          }}
        />
      )}

      {/* Direct Arqueo Creation Modal */}
      {showArqueoModal && (
        <CreateCajaRecord
          show={showArqueoModal}
          setShow={setShowArqueoModal}
          onRecordCreated={() => {
            setShowArqueoModal(false);
            // Remove arqueo from pending categories
            setCategories(prev => prev.filter(c => c.id !== 'arqueo'));
          }}
        />
      )}
    </>
  );
};

export default PendingTable;
